import {createMuiTheme} from '@material-ui/core/styles';
import createBreakpoints from '@material-ui/core/styles/createBreakpoints';

import {ANIMATION_DEFINITIONS, ANIMATION_IDS} from '@makolab/library-react/dist/jssAnimations';
import {baseThemeConfig} from '@makolab/library-react/dist/jssConfig';

import {publicUrl} from '../commons/uri';

const breakpointsValues = {
  values: {
    xs: 340,
    headerExtraBreakpoint390: 390,
    headerExtraBreakpoint500: 500,
    sm: 670,
    md: 1024,
    lg: 1240,
    xl: 1920,
  },
};

const breakpoints = createBreakpoints(breakpointsValues);

// https://material-ui.com/customization/themes/

const palette = {
  background: {
    light: '#FFFFFF',
    gray: '#F2F2F2',
    label: '#FF671B',
    icon: '#153450',
  },
  primary: {
    legalNote: '#777',
    main: '#646b52',
    contrastText: '#ffffff',
    bodyText: '#000000',
    textFieldBottomBorderColor: '#D8D8D8',
  },
  secondary: {
    main: '#000',
    contrastText: '#ffffff',
  },
  neutral: {
    light: '#f2f2f2',
    medium: '#cccccc',
    base: '#777777',
    dark: '#333333',
  },
};

const form = {
  field: {
    width: '100%',
    marginBottom: 37,
    color: '#3E3F40',
    fontSize: '16px',

    [breakpoints.up('md')]: {
      width: 'calc(100% - 30px)',
      marginBottom: 40,
    },
  },
  input: {
    backgroundColor: 'transparent',

    [breakpoints.up('md')]: {
      marginRight: 30,
    },

    '& label': {
      color: '#3E3F40',
      fontWeight: 500,
    },
  },
  select: {
    marginTop: 24,
    marginBottom: 0,

    [breakpoints.up('md')]: {
      marginTop: 17,
      marginBottom: 58,
    },
  },
  radioGroup: {
    fontFamily: 'Read',
    margin: '6px 0px',
  },
  textarea: {
    marginTop: 60,

    [breakpoints.up('md')]: {
      marginTop: 0,
    },
  },
};

const brandSpecificIncjections = {
  gridItemSubmit: {
    color: '#646B52',
  },
  customHeader: {
    fontSize: '14px',
    height: 55,

    '&>div': {
      paddingTop: '18px',
      paddingLeft: '120px',

      [breakpoints.up('headerExtraBreakpoint390')]: {
        paddingLeft: '75px',
      },

      [breakpoints.up('headerExtraBreakpoint500')]: {
        paddingLeft: '0px',
      },

      [breakpoints.up('md')]: {
        paddingTop: '24px',
      },
    },

    [breakpoints.up('md')]: {
      height: 80,
      fontSize: '24px',
    },
  },
  subHeader: {
    borderTop: `1px solid #FAFAFA`,
  },
  legalNote: {
    color: palette.primary.legalNote,
    fontSize: 12,
    lineHeight: '16px',
    fontFamily: 'Read',
  },
  marketingNote: {
    fontFamily: 'Read',
  },
  heading: {
    fontSize: 16,
    lineHeight: '29px',
  },
};

export const themeConfig = {
  ...baseThemeConfig,
  palette,
  form,
  brandSpecificIncjections,
  breakpoints: breakpointsValues,
  spacing: 8,
  typography: {
    fontSize: 14,
    useNextVariants: true,
    fontFamily: ['Read', 'Arial'].join(','),
    // base pixel size 14px
    h1: {
      fontFamily: 'DaciaBlock-Bold',
      color: palette.primary.contrastText,
      fontSize: '2.571em', // 36px
      textTransform: 'uppercase',
      [breakpoints.up('md')]: {
        fontSize: '3.85em', // 54px
      },
    },
    h2: {
      fontFamily: 'DaciaBlock-Bold',
      color: palette.primary.main,
      fontSize: '2em', // 28px,
      textTransform: 'uppercase',
    },
    h3: {
      fontFamily: 'DaciaBlock-Bold',
      color: palette.primary.main,
      fontSize: '1.714em', // 24px
      textTransform: 'uppercase',
    },
    h4: {
      fontFamily: 'DaciaBlock-Bold',
      fontWeight: 'inherit',
    },
    h5: {
      fontFamily: 'DaciaBlock-Bold',
      fontWeight: 'inherit',
    },
    h6: {
      fontFamily: 'DaciaBlock-Bold',
      color: palette.primary.main,
      lineHeight: '26px',
      fontSize: '1.429em', // 20px
    },
    subtitle1: {
      fontFamily: 'DaciaBlock-Regular', // Normal
      lineHeight: '28px',
      color: palette.primary.main,
      fontSize: 'inherit',
    },
    subtitle2: {
      fontFamily: 'DaciaBlock-Light', // Light
      lineHeight: '28px',
      color: palette.primary.main,
      fontSize: 'inherit',
    },
    body1: {
      fontFamily: 'DaciaBlock-Regular',
      fontWeight: 700,
      lineHeight: '24px',
      color: palette.primary.bodyText,
      fontSize: 'inherit',
    },
    body2: {
      fontFamily: 'DaciaBlock-Regular',
      fontWeight: 500,
      lineHeight: '24px',
      color: palette.primary.bodyText,
      fontSize: 'inherit',
    },
  },
  mlbCustomVariables: { // put your custom project theme variables here
    animationIds: {...ANIMATION_IDS},
    animationDefs: {...ANIMATION_DEFINITIONS},
    svgIconUrl: publicUrl('/static/svg/dacia-one-icons.svg'), // theme.mlbCustomVariables.svgIconUrl
    publicUrl,
  },
  overrides: {
    MuiButton: {
      root: {
        borderRadius: '0px',
        minWidth: '150px',
        height: '46px',
      },
      label: {
        fontSize: '.9em',
        fontWeight: 600,
        textTransform: 'uppercase',
      },
      contained: {
        boxShadow: 'none',
        '&:active': {
          boxShadow: 'none',
        },
      },
      outlined: {
        border: '2px solid',
      },
      outlinedPrimary: {
        border: '2px solid',
        borderColor: palette.primary.main,
        '@media (hover: none)': {
          '&:hover': {
            border: '2px solid',
            borderColor: palette.secondary.main,
            color: palette.primary.main,
          },
        },
        '&:hover': {
          border: '2px solid',
          borderColor: palette.secondary.main,
          backgroundColor: palette.secondary.main,
          color: palette.primary.contrastText,
        },
      },
      outlinedSecondary: {
        border: '2px solid',
        borderColor: palette.primary.contrastText,
        color: palette.primary.contrastText,
        '@media (hover: none)': {
          '&:hover': {
            border: '2px solid',
            borderColor: palette.primary.contrastText,
            color: palette.primary.contrastText,
          },
        },
        '&:hover': {
          border: '2px solid',
          borderColor: palette.primary.contrastText,
          backgroundColor: palette.primary.contrastText,
          color: palette.primary.main,
        },
      },
    },
    MuiFormControl: {
      root: {
        width: '100%',
      },
    },
    MuiFormControlLabel: {
      label: {
        fontFamily: 'Read',
      },
    },
    MuiInput: {
      underline: {
        '&&&&:hover:before': {
          borderBottom: '0px',
        },
        '&:before': {
          borderBottom: '0px',
        },
        '&:after': {
          borderBottom: `0px`,
        },
      },
    },
    MuiInputLabel: {
      formControl: {
        transform: 'none',
      },
      outlined: {
        transform: 'translate(10px, 10px) scale(1)',
        fontWeight: 500,
      },
    },
    MuiInputBase: {
      root: {
        fontWeight: 500,
        color: '#3E3F40',
      },
      input: {
        borderBottom: `1px solid ${palette.primary.textFieldBottomBorderColor}`,
      },
      inputMultiline: {
        borderBottomWidth: 0,
      },
    },
    MuiFormLabel: {
      root: {
        color: '#3E3F40',
        '&$focused': {
          color: '#000',
        },
      },
    },
    MuiSelect: {
      select: {
        '&:focus': {
          backgroundColor: 'transparent',
        },
      },
    },
    MuiSvgIcon: {
      root: {
        fontSize: '31px',
      },
    },
    MuiTab: {
      root: {
        fontSize: '1.143em',
        [breakpoints.down('xs')]: {
          width: '100%',
          maxWidth: '100%',
        },
        [breakpoints.up('xs')]: {
          fontSize: '1.143em',
        },
      },
      fullWidth: {
        flexGrow: 0,
      },
      labelContainer: {
        [breakpoints.down('sm')]: {
          padding: 0,
        },
        [breakpoints.up('sm')]: {
          padding: '6px 8px',
        },
      },
      textColorInherit: {
        opacity: '1',
      },
    },
    MuiTabs: {
      flexContainer: {
        justifyContent: 'center',
      },
    },
    MuiPrivateTabIndicator: {
      colorSecondary: {
        backgroundColor: 'transparent',
      },
    },
    MuiPrivateNotchedOutline: {
      root: {
        borderRadius: 0,
      },
    },
    MuiOutlinedInput: {
      multiline: {
        padding: 10,
      },
    },
  },
};

export default createMuiTheme(themeConfig);



// WEBPACK FOOTER //
// ./src/styles/theme-dacia.js